// Continuous-practice controller. Turns coach mode into a self-paced loop: when an
// exercise is mastered it pauses and holds the result up until you press any key (or
// ▶ Continue), then loads and starts the next curriculum lesson; and it owns the C♯2
// master toggle that
// switches the whole experience on/off. The engine and the Coach are unaware of
// it (same posture as the Coach over the engine) — `main.ts` injects the seams
// and calls `tick()` once per frame.

import type { CatalogEntry } from "./songs/catalog";
import { clearCoachProgress, weakestMasteredId } from "./store/coach-progress";
import type { UIMode } from "./store/prefs";
import { computePlayNext } from "./ui/play-next";

/** A mastered lesson, blocked on a "press any key / ▶ Continue" gate before it loads.
 *  No timer — the player resumes when ready, so feedback stays up as long as needed. */
interface Pending {
  targetId: string;
  /** Display name shown in the gate status. */
  label: string;
  /** Reset the target's saved progress before loading (weakest-replay). */
  replay: boolean;
}

interface Target {
  id: string;
  label: string;
  replay: boolean;
}

/** The seams `main.ts` provides; CoachSession owns no DOM/engine state itself. */
export interface CoachSessionHost {
  hasSong(): boolean;
  isPlaying(): boolean;
  coachDone(): boolean;
  mode(): UIMode;
  /** The loaded catalog (for the sequencer + title lookup). */
  catalog(): readonly CatalogEntry[];
  /** Apply a play mode (engine + drawer select + (de)attach coach + pause). */
  applyMode(mode: UIMode): void;
  loadSong(id: string): Promise<void>;
  /** Start / stop playback of the current song (engine + the Play button). */
  setPlaying(playing: boolean): void;
  setStatus(text: string): void;
  /** The ▶ Next button (text + id; null hides it). */
  setPlayNext(text: string | null, id?: string): void;
}

export class CoachSession {
  private pending: Pending | null = null;
  /** Edge latch so a single `coach.done` fires the advance once. */
  private wasDone = false;
  /** The mode to restore when the master toggle goes off. */
  private previousMode: UIMode = "wait";

  constructor(private readonly host: CoachSessionHost) {}

  /** Reset the done-edge latch + any pending gate when a coach (re)attaches. */
  reset(): void {
    this.wasDone = false;
    this.cancel();
  }

  /** Cancel a pending continue gate (manual play / mode change / song change). */
  cancel(): void {
    if (!this.pending) return;
    this.pending = null;
    this.refreshPlayNext();
  }

  /** Enter the running loop: switch to coach and start playing immediately,
   *  loading the next target first if nothing is loaded. Coach mode is a *running*
   *  loop — there is no paused-coach state. Idempotent re the remembered mode. */
  enter(): void {
    if (this.host.mode() !== "coach") this.previousMode = this.host.mode();
    this.host.applyMode("coach"); // attaches the coach + pauses (main calls reset())
    if (this.host.hasSong()) {
      this.host.setPlaying(true);
      return;
    }
    const target = this.nextTarget();
    if (target) void this.advance(target);
    else this.host.setStatus("Coach: load a song or drill to begin.");
  }

  /** Exit the loop: cancel any countdown and revert to the previous manual mode
   *  (left paused). Pausing while coaching routes here — pause *is* exit. */
  exit(): void {
    this.cancel();
    this.host.applyMode(this.previousMode === "coach" ? "wait" : this.previousMode);
  }

  /** C♯2 master toggle: not coaching → enter (play); coaching → exit (paused). */
  toggle(): void {
    if (this.host.mode() === "coach") this.exit();
    else this.enter();
  }

  /** Pass the pending gate and load the next lesson now — the ▶ Continue button, or
   *  any key while a mastered lesson is waiting. Returns true if it consumed a gate. */
  startNow(): boolean {
    const p = this.pending;
    if (!p) return false;
    this.pending = null;
    void this.advance({ id: p.targetId, label: p.label, replay: p.replay });
    return true;
  }

  /** Once per frame, after the coach has stepped. */
  tick(): void {
    if (this.pending) return; // blocked on the continue gate — wait for a key / ▶ Continue
    const done = this.host.mode() === "coach" && this.host.coachDone();
    if (done && !this.wasDone && this.host.isPlaying()) this.onExerciseMastered();
    this.wasDone = done;
  }

  private onExerciseMastered(): void {
    this.host.setPlaying(false);
    const target = this.nextTarget();
    if (!target) {
      this.host.setStatus("Mastered! Nothing new to practice yet.");
      return;
    }
    this.pending = { targetId: target.id, label: target.label, replay: target.replay };
    this.host.setPlayNext("▶ Continue", target.id);
    this.host.setStatus(`Mastered! Next: ${target.label} — press any key to continue`);
  }

  private async advance(target: Target): Promise<void> {
    if (target.replay) clearCoachProgress(target.id);
    await this.host.loadSong(target.id); // re-attaches the coach (main calls reset())
    this.host.setPlaying(true);
  }

  /** The next lesson: the sequencer's suggestion, else replay the weakest. */
  private nextTarget(): Target | null {
    const next = computePlayNext(this.host.catalog());
    if (next) return { id: next.id, label: this.titleOf(next.id), replay: false };
    const weakId = weakestMasteredId();
    return weakId ? { id: weakId, label: this.titleOf(weakId), replay: true } : null;
  }

  private titleOf(id: string): string {
    return this.host.catalog().find((e) => e.id === id)?.title ?? id;
  }

  /** Recompute the ▶ Next suggestion from current mastery (also used on load). */
  refreshPlayNext(): void {
    const n = computePlayNext(this.host.catalog());
    this.host.setPlayNext(n?.text ?? null, n?.id);
  }
}
